import { Grid2, Typography } from '@mui/material';
import { COLORS, DATE_FORMATS } from '../../utils/Application_Constants';
import { formatIntegerOrDecimal } from '../../utils/formatIntegerOrDecimal';
import { renderCommentToolTip } from './RenderCommentToolTip';
import { formatUtcDate } from '../../utils/formatDate';
import { currencyFormatter } from '../../utils/helpers';
import { rateTypesEnumApiToDisplay } from '../../Admin/Configurations/processedTimesheetsConfig';

const fillEmptyProcessedTimesheetEntriesValues = (row) => ({
    ...row,
    shiftType: row.shiftType || '-',
    startTime: row.startTime || '-',
    endTime: row.endTime || '-',
    hours: row.hours || '-',
    units: row.units || '-',
    miles: row.miles || '-',
    comments:
        typeof row.comments === 'string' && row.comments.trim() !== ''
            ? row.comments
            : 'No Comments',
});

const formatRowsByRelatedEntryId = (rows = []) => {
    const childrenMap = new Map();
    const normalizedParents = [];

    rows.forEach((row) => {
        const { relatedEntryId } = row;

        if (relatedEntryId == null) {
            normalizedParents.push({ ...row, isChild: false });
        } else {
            if (!childrenMap.has(relatedEntryId)) {
                childrenMap.set(relatedEntryId, []);
            }
            childrenMap.get(relatedEntryId).push(row);
        }
    });

    const orderedRows = [];

    normalizedParents.forEach((parent, parentIndex) => {
        const groupClass = parentIndex % 2 === 0 ? 'even-row' : 'odd-row';
        const parentId = parent.id ?? parentIndex;

        orderedRows.push({
            ...parent,
            showGroupValue: true,
            groupClass,
            isChild: false,
            id: parentId,
            units: formatIntegerOrDecimal(parent?.units),
            hours: formatIntegerOrDecimal(parent?.hours),
            miles: formatIntegerOrDecimal(parent?.miles),
        });

        const children = childrenMap.get(parent.id) || [];
        children.sort((a, b) => new Date(a.date) - new Date(b.date));

        children.forEach((child, childIndex) => {
            const childDate = child?.date ?? '';
            const parentDate = parent?.date ?? '';
            const formattedDate = childDate === parentDate ? '' : childDate;

            orderedRows.push({
                ...child,
                showGroupValue: true,
                groupClass,
                isChild: true,
                id: `${parent.id}-${childIndex}`,
                date: formattedDate,
                miles: '-',
                hours: formatIntegerOrDecimal(child?.hours),
            });
        });
    });

    return orderedRows;
};

const processedTimesheetEntriesTableColumns = [
    {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
        renderCell: (params) => <strong>{params.value}</strong>,
    },
    {
        field: 'shiftType',
        headerName: 'Shift Type',
        flex: 2,
        headerClassName: 'custom-header',
        sortable: false,
    },
    {
        field: 'units',
        headerName: 'Units',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
    },
    {
        field: 'startTime',
        headerName: 'Start Time',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
    },
    {
        field: 'endTime',
        headerName: 'End Time',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
    },
    {
        field: 'hours',
        headerName: 'Hours',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
    },
    {
        field: 'miles',
        headerName: 'Miles',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
    },
    {
        field: 'comments',
        headerName: 'Comments',
        flex: 2,
        headerClassName: 'custom-header',
        sortable: false,
        renderCell: (params) => (
            <Grid2
                container
                alignItems="center"
                justifyContent="flex-start"
                sx={{
                    height: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        color:
                            params.value && params.value !== 'No Comments'
                                ? 'inherit'
                                : COLORS.LT_MIDNIGHT50,
                    }}
                >
                    {params.value
                        ? renderCommentToolTip(params.value.replace('Mileage:', '<b>Mileage:</b>'))
                        : 'No Comments'}
                </Typography>
            </Grid2>
        ),
    },
];

const processedTimesheetDetailsTableColumns = [
    {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        headerClassName: 'custom-header',
        cellClassName: 'bold-text',
        sortable: true,
        valueFormatter: (value) => formatUtcDate(value, DATE_FORMATS.WEEKDAY_MD),
    },
    {
        field: 'payTypeId',
        headerName: 'Pay Type ID',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: true,
    },
    {
        field: 'rateType',
        headerName: 'Rate Type',
        flex: 2,
        headerClassName: 'custom-header',
        sortable: true,
    },
    {
        field: 'billTime',
        headerName: 'Bill Time/Units',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
        valueFormatter: (value) => {
            const parsedValue = parseFloat(value);
            return !Number.isNaN(parsedValue) && parsedValue !== 0 ? parsedValue.toFixed(2) : '-';
        },
    },
    {
        field: 'billUnit',
        headerName: 'Bill Unit',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
        valueFormatter: (value) => rateTypesEnumApiToDisplay(value),
    },
    {
        field: 'billAmount',
        headerName: 'Bill Amount',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
        valueFormatter: (value) => {
            const parsedValue = parseFloat(value);
            return !Number.isNaN(parsedValue) && parsedValue !== 0
                ? currencyFormatter(parsedValue.toFixed(2))
                : '-';
        },
    },
    {
        field: 'payTime',
        headerName: 'Pay Time/Units',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
        valueFormatter: (value) => {
            const parsedValue = parseFloat(value);
            return !Number.isNaN(parsedValue) && parsedValue !== 0 ? parsedValue.toFixed(2) : '-';
        },
    },
    {
        field: 'payUnit',
        headerName: 'Pay Unit',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
        valueFormatter: (value) => rateTypesEnumApiToDisplay(value),
    },
    {
        field: 'payAmount',
        headerName: 'Pay Amount',
        flex: 1,
        headerClassName: 'custom-header',
        sortable: false,
        valueFormatter: (value) => {
            const parsedValue = parseFloat(value);
            return !Number.isNaN(parsedValue) && parsedValue !== 0
                ? currencyFormatter(parsedValue.toFixed(2))
                : '-';
        },
    },
];

export {
    fillEmptyProcessedTimesheetEntriesValues,
    formatRowsByRelatedEntryId,
    processedTimesheetEntriesTableColumns,
    processedTimesheetDetailsTableColumns,
};
